import { useEffect, useRef, useState, type PropsWithChildren } from "react";

interface RowMenuProps extends PropsWithChildren {
  // Named for screen readers and shown as the control's tooltip.
  label?: string;
}

// One disclosure per list row instead of a pair of permanent micro-buttons.
// The row stays readable, the actions stay one click away, and the contents
// only enter the DOM while the menu is open — a list of 200 rows carries 200
// controls, not 600.
export function RowMenu({ label = "Row actions", children }: RowMenuProps) {
  const holder = useRef<HTMLDetailsElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    function close(event: Event) {
      const element = holder.current;
      if (!element) return;
      if (event.type === "keydown") {
        if ((event as KeyboardEvent).key !== "Escape") return;
        setOpen(false);
        element.querySelector("summary")?.focus();
        return;
      }
      if (!element.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  return (
    <details className="row-menu" ref={holder} open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary aria-label={label} title={label}><span aria-hidden="true">•••</span></summary>
      {open ? (
        // Choosing an action dismisses the menu. Without this the panel stays
        // open behind whatever the action opened, and the row underneath is
        // still covered when that dialog closes.
        <div
          className="row-menu-panel" role="group" aria-label={label}
          onClick={(event) => { if ((event.target as HTMLElement).closest("button")) setOpen(false); }}
        >
          {children}
        </div>
      ) : null}
    </details>
  );
}
